import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import CountUp from 'react-countup';
import {
  ShieldCheck, ShieldAlert, Play, Download, RefreshCw, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, AlertTriangle, Clock, Loader2,
  Bug, Lock, FileWarning, Globe, Users as UsersIcon, KeyRound, Database, Search as SearchIcon,
  Terminal, Fingerprint, Timer, Zap, HardDrive, Wifi, ShieldOff, Eye, Cookie, Trash2, Filter, X,
} from 'lucide-react';
import { usePageHeader } from '../components/Layout';
import { SectionCard, AreaChart, Donut } from '../design/primitives';

const API = `${window.location.origin}/api`;

const CATEGORY_CONFIG = {
  nosql_injection: { label: 'NoSQL Injection', icon: Database },
  xss: { label: 'XSS', icon: Bug },
  auth_bypass: { label: 'Auth Bypass', icon: KeyRound },
  command_injection: { label: 'CMD Injection', icon: Terminal },
  jwt_manipulation: { label: 'JWT Manipülasyonu', icon: Fingerprint },
  session_fixation: { label: 'Session Fixation', icon: ShieldOff },
  idor: { label: 'IDOR', icon: SearchIcon },
  brute_force: { label: 'Brute Force', icon: Lock },
  rate_limiting: { label: 'Rate Limiting', icon: Timer },
  stress_test: { label: 'Stres Testi', icon: Zap },
  file_security: { label: 'Dosya Güvenliği', icon: FileWarning },
  file_size_limit: { label: 'Dosya Boyut Limiti', icon: HardDrive },
  ssrf: { label: 'SSRF', icon: Wifi },
  password_policy: { label: 'Şifre Politikası', icon: Lock },
  permission_bypass: { label: 'Yetki Bypass', icon: UsersIcon },
  method_tampering: { label: 'Method Tampering', icon: ShieldAlert },
  header_security: { label: 'Header Güvenliği', icon: Globe },
  data_leakage: { label: 'Veri Sızıntısı', icon: Eye },
  cookie_security: { label: 'Cookie Güvenliği', icon: Cookie },
};

const STATUS_META = {
  pass: { label: 'Başarılı', icon: CheckCircle2, color: 'var(--good)', weak: 'var(--good-weak)' },
  fail: { label: 'Başarısız', icon: XCircle, color: 'var(--danger)', weak: 'var(--danger-weak)' },
  warning: { label: 'Uyarı', icon: AlertTriangle, color: 'var(--warn)', weak: 'var(--warn-weak)' },
};

const SEVERITY_COLORS = {
  critical: 'var(--danger)',
  high: 'var(--warn)',
  medium: 'var(--accent)',
  low: 'var(--text-muted)',
};

export default function SecurityScan() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'super_admin') navigate('/dashboard');
  }, [user, navigate]);

  const fetchScans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/security/scans?page=${page}&limit=20`);
      setScans(res.data.scans);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
    } catch {
      toast.error('Tarama geçmişi yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page]);

  const fetchLatest = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/security/scans/latest`);
      if (res.data) setSelectedScan(res.data);
    } catch { /* no latest scan */ }
  }, []);

  useEffect(() => { fetchScans(); }, [fetchScans]);
  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  useEffect(() => {
    if (!scanning) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/security/scans?page=1&limit=1`);
        const latest = res.data.scans[0];
        if (latest && latest.status === 'completed') {
          setScanning(false);
          const detail = await axios.get(`${API}/security/scans/${latest.id}`);
          setSelectedScan(detail.data);
          fetchScans();
          toast.success('Güvenlik testi tamamlandı!');
        }
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [scanning, fetchScans]);

  const startScan = async () => {
    try {
      setScanning(true);
      await axios.post(`${API}/security/scan`);
      toast.info('Güvenlik testi başlatıldı...');
    } catch (err) {
      setScanning(false);
      toast.error(err.response?.data?.detail || 'Test başlatılamadı');
    }
  };

  const viewScan = async (scanId) => {
    try {
      const res = await axios.get(`${API}/security/scans/${scanId}`);
      setSelectedScan(res.data);
    } catch {
      toast.error('Tarama detayı yüklenemedi');
    }
  };

  const downloadPdf = async (scanId) => {
    try {
      const res = await axios.get(`${API}/security/scans/${scanId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `onxflow_guvenlik_raporu_${scanId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF raporu indirildi');
    } catch {
      toast.error('PDF indirilemedi');
    }
  };

  const deleteScan = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/security/scans/${confirmDelete}`);
      toast.success('Tarama kaydı silindi');
      if (selectedScan?.id === confirmDelete) setSelectedScan(null);
      setConfirmDelete(null);
      fetchScans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tarama silinemedi');
    }
  };

  const headerActions = useMemo(() => (
    <>
      <button className="ox-btn" onClick={() => { fetchScans(); fetchLatest(); }}>
        <RefreshCw size={14} style={{ marginRight: 6 }} />
        Yenile
      </button>
      <button className="ox-btn ox-btn-primary" onClick={startScan} disabled={scanning}>
        {scanning ? <Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} /> : <Play size={14} style={{ marginRight: 6 }} />}
        {scanning ? 'Taranıyor...' : 'Test Başlat'}
      </button>
    </>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [scanning]);

  usePageHeader([{ label: 'Güvenlik Testi' }], headerActions, [scanning]);

  const summary = selectedScan?.summary;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const results = selectedScan?.results || [];

  const filteredResults = results.filter(r => {
    if (filterCategory !== 'all' && r.category !== filterCategory) return false;
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    return true;
  });

  const pieData = summary
    ? [
        { name: 'Başarılı', value: summary.passed || 0, color: 'var(--good)' },
        { name: 'Başarısız', value: summary.failed || 0, color: 'var(--danger)' },
        { name: 'Uyarı', value: summary.warnings || 0, color: 'var(--warn)' },
      ].filter(d => d.value > 0)
    : [];

  const categoryBreakdown = useMemo(() => {
    if (!results.length) return [];
    const map = {};
    results.forEach(r => {
      const label = CATEGORY_CONFIG[r.category]?.label || r.category;
      if (!map[label]) map[label] = { label, pass: 0, fail: 0, warning: 0, total: 0 };
      if (map[label][r.status] != null) map[label][r.status]++;
      map[label].total++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [results]);

  const trendData = scans
    .filter(s => s.status === 'completed' && s.summary)
    .slice(0, 10)
    .reverse()
    .map(s => ({
      d: s.completedAt ? s.completedAt.split('T')[0].split('-').slice(1).join('/') : '',
      v: s.summary?.score || 0,
    }));

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (!user || user.role !== 'super_admin') return null;

  if (loading && scans.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin mx-auto mb-3" style={{ color: 'var(--accent)' }} />
          <div className="ox-text-muted text-sm">Güvenlik testi yükleniyor...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {scanning && (
        <div className="ox-surface rounded-md p-4 flex items-center gap-3" style={{ borderColor: 'var(--accent)' }}>
          <Loader2 size={18} className="animate-spin" style={{ color: 'var(--accent)' }} />
          <div className="flex-1">
            <div className="text-sm font-medium">Güvenlik Testi Devam Ediyor</div>
            <div className="text-xs ox-text-muted mt-0.5">NoSQL Injection, XSS, Auth Bypass, IDOR, Brute Force ve diğer testler çalışıyor...</div>
            <div className="mt-2 ox-track">
              <div style={{ width: '60%', height: '100%', background: 'var(--accent)', opacity: 0.7 }} />
            </div>
          </div>
        </div>
      )}

      {/* Summary KPIs */}
      {summary && (
        <>
          <div className="flex items-center gap-2 text-xs ox-text-muted">
            <ShieldCheck size={14} />
            Son test: {formatDate(selectedScan?.completedAt)}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <KpiTile label="Güvenlik Skoru" value={summary.score} suffix="%" color={summary.score >= 80 ? 'var(--good)' : summary.score >= 50 ? 'var(--warn)' : 'var(--danger)'} />
            <KpiTile label="Toplam Test" value={summary.total} />
            <KpiTile label="Başarılı" value={summary.passed} color="var(--good)" />
            <KpiTile label="Başarısız" value={summary.failed} color="var(--danger)" />
            <KpiTile label="Uyarı" value={summary.warnings} color="var(--warn)" />
          </div>
        </>
      )}

      {/* Charts */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {trendData.length > 1 && (
            <SectionCard title="Skor Trendi" subtitle="Son 10 tamamlanan test" className="lg:col-span-3">
              <AreaChart data={trendData} height={160} color="var(--accent)" />
            </SectionCard>
          )}

          <SectionCard title="Sonuç Dağılımı">
            {pieData.length > 0 ? (
              <div className="flex items-center gap-4 p-2">
                <Donut
                  segments={pieData.map(d => ({ value: d.value, color: d.color }))}
                  size={140}
                  thickness={16}
                  centerValue={summary.total}
                  centerLabel="toplam"
                />
                <div className="flex-1 space-y-2">
                  {pieData.map(d => (
                    <div key={d.name} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: d.color, display: 'inline-block' }} />
                        <span>{d.name}</span>
                      </div>
                      <span className="ox-mono ox-tnum font-semibold">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyBlock text="Veri yok" height={160} />
            )}
          </SectionCard>

          <SectionCard title="Kategori Bazlı Sonuçlar" className="lg:col-span-2">
            {categoryBreakdown.length > 0 ? (
              <div className="space-y-2">
                {categoryBreakdown.slice(0, 10).map(cat => (
                  <div key={cat.label} className="flex items-center gap-3">
                    <div className="text-xs flex-1 truncate" title={cat.label}>{cat.label}</div>
                    <div className="flex-[2] flex h-2 rounded overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                      {cat.pass > 0 && <div style={{ width: `${(cat.pass / cat.total) * 100}%`, background: 'var(--good)' }} />}
                      {cat.warning > 0 && <div style={{ width: `${(cat.warning / cat.total) * 100}%`, background: 'var(--warn)' }} />}
                      {cat.fail > 0 && <div style={{ width: `${(cat.fail / cat.total) * 100}%`, background: 'var(--danger)' }} />}
                    </div>
                    <div className="ox-mono text-[11px] ox-text-muted w-24 text-right">
                      <span style={{ color: 'var(--good)' }}>{cat.pass}</span>
                      {' / '}
                      <span style={{ color: 'var(--danger)' }}>{cat.fail}</span>
                      {' / '}
                      <span style={{ color: 'var(--warn)' }}>{cat.warning}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyBlock text="Veri yok" height={160} />
            )}
          </SectionCard>
        </div>
      )}

      {/* Detailed Results */}
      {results.length > 0 && (
        <SectionCard
          title={`Detaylı Test Sonuçları (${filteredResults.length})`}
          right={selectedScan?.id && (
            <button className="ox-btn" onClick={() => downloadPdf(selectedScan.id)}>
              <Download size={14} style={{ marginRight: 6 }} />
              PDF İndir
            </button>
          )}
          pad={false}
        >
          <div className="p-3 flex flex-wrap items-center gap-2" style={{ borderBottom: '1px solid var(--border)' }}>
            <Filter size={14} style={{ color: 'var(--text-muted)' }} />
            <select className="ox-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="all">Tüm Kategoriler</option>
              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
            <select className="ox-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">Tüm Durumlar</option>
              <option value="pass">Başarılı</option>
              <option value="fail">Başarısız</option>
              <option value="warning">Uyarı</option>
            </select>
            {(filterCategory !== 'all' || filterStatus !== 'all') && (
              <button className="ox-btn ox-btn-ghost" onClick={() => { setFilterCategory('all'); setFilterStatus('all'); }}>
                <X size={12} style={{ marginRight: 4 }} />
                Temizle
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider ox-text-dim" style={{ borderBottom: '1px solid var(--border)' }}>
                  <th className="text-left px-3 py-2 font-medium">Durum</th>
                  <th className="text-left px-3 py-2 font-medium">Kategori</th>
                  <th className="text-left px-3 py-2 font-medium">Test</th>
                  <th className="text-left px-3 py-2 font-medium">Detay</th>
                  <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Önem</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((r) => {
                  const st = STATUS_META[r.status] || STATUS_META.warning;
                  const StIcon = st.icon;
                  const catCfg = CATEGORY_CONFIG[r.category] || { label: r.category, icon: ShieldAlert };
                  const CatIcon = catCfg.icon;
                  return (
                    <tr key={r.id} className="ox-row-hover" style={{ borderBottom: '1px solid var(--border)' }}>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px]" style={{ background: st.weak, color: st.color }}>
                          <StIcon size={12} />
                          {st.label}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="inline-flex items-center gap-1.5 text-[12px] ox-text-muted">
                          <CatIcon size={12} />
                          {catCfg.label}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-medium">{r.testName}</td>
                      <td className="px-3 py-2 ox-text-muted text-[12px] max-w-md truncate" title={r.detail}>{r.detail}</td>
                      <td className="px-3 py-2 hidden md:table-cell">
                        <span className="text-[11px] uppercase font-semibold" style={{ color: SEVERITY_COLORS[r.severity] || 'var(--text-muted)' }}>
                          {r.severity}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {/* Scan History */}
      <SectionCard title={`Test Geçmişi (${total})`} pad={false}>
        {scans.length === 0 ? (
          <div className="py-12 text-center">
            <ShieldCheck size={36} className="mx-auto mb-3 opacity-30" />
            <div className="text-sm font-medium">Henüz Test Yapılmamış</div>
            <div className="text-xs ox-text-muted mt-1">İlk güvenlik testinizi başlatmak için yukarıdaki butona tıklayın.</div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider ox-text-dim" style={{ borderBottom: '1px solid var(--border)' }}>
                    <th className="text-left px-3 py-2 font-medium">Tarih</th>
                    <th className="text-left px-3 py-2 font-medium">Durum</th>
                    <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Skor</th>
                    <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Testler</th>
                    <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Süre</th>
                    <th className="text-right px-3 py-2 font-medium">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {scans.map(scan => (
                    <tr key={scan.id} className="ox-row-hover" style={{ borderBottom: '1px solid var(--border)' }}>
                      <td className="px-3 py-2">
                        <div className="text-[13px]">{formatDate(scan.createdAt)}</div>
                        <div className="text-[11px] ox-text-dim">{scan.startedByName}</div>
                      </td>
                      <td className="px-3 py-2">
                        {scan.status === 'completed' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px]" style={{ background: 'var(--good-weak)', color: 'var(--good)' }}>
                            <CheckCircle2 size={12} /> Tamamlandı
                          </span>
                        ) : scan.status === 'running' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px]" style={{ background: 'var(--accent-weak)', color: 'var(--accent)' }}>
                            <Loader2 size={12} className="animate-spin" /> Çalışıyor
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px]" style={{ background: 'var(--surface-2)', color: 'var(--text-muted)' }}>
                            <Clock size={12} /> Bekliyor
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 hidden sm:table-cell">
                        {scan.summary ? (
                          <span className="ox-mono ox-tnum font-semibold" style={{ color: scan.summary.score >= 80 ? 'var(--good)' : scan.summary.score >= 50 ? 'var(--warn)' : 'var(--danger)' }}>
                            {scan.summary.score}%
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell ox-mono text-[12px]">
                        {scan.summary ? (
                          <>
                            <span style={{ color: 'var(--good)' }}>{scan.summary.passed}</span>
                            {' / '}
                            <span style={{ color: 'var(--danger)' }}>{scan.summary.failed}</span>
                            {' / '}
                            <span style={{ color: 'var(--warn)' }}>{scan.summary.warnings}</span>
                          </>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell ox-text-muted text-[12px]">
                        {scan.duration ? `${scan.duration}s` : '-'}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          {scan.status === 'completed' && (
                            <>
                              <button className="ox-btn ox-btn-ghost text-xs" onClick={() => viewScan(scan.id)}>Detay</button>
                              <button className="ox-btn ox-btn-ghost" onClick={() => downloadPdf(scan.id)} title="PDF indir">
                                <Download size={13} />
                              </button>
                              <button
                                className="ox-btn ox-btn-ghost"
                                style={{ color: 'var(--danger)' }}
                                onClick={() => setConfirmDelete(scan.id)}
                                title="Sil"
                              >
                                <Trash2 size={13} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-3" style={{ borderTop: '1px solid var(--border)' }}>
                <span className="text-xs ox-text-muted">Toplam {total} test</span>
                <div className="flex items-center gap-1">
                  <button className="ox-btn ox-btn-ghost" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                    <ChevronLeft size={14} />
                  </button>
                  <span className="ox-mono text-xs px-2">
                    {page} / {totalPages}
                  </span>
                  <button className="ox-btn ox-btn-ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </SectionCard>

      {confirmDelete && (
        <div className="ox-modal-backdrop" onClick={() => setConfirmDelete(null)}>
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="text-sm font-semibold">Taramayı sil</div>
              <button className="ox-btn ox-btn-ghost" onClick={() => setConfirmDelete(null)}><X size={14} /></button>
            </div>
            <div className="ox-modal-body">
              <div className="text-sm ox-text-muted">Bu tarama kaydını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</div>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn" onClick={() => setConfirmDelete(null)}>İptal</button>
              <button className="ox-btn ox-btn-danger" onClick={deleteScan}>Sil</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, suffix, color }) {
  return (
    <div className="ox-surface rounded-md p-4">
      <div className="text-[11.5px] ox-text-muted uppercase tracking-wider" style={{ letterSpacing: '.04em' }}>{label}</div>
      <div className="ox-tnum font-semibold leading-none mt-2" style={{ fontSize: 32, letterSpacing: -0.6, color: color || 'var(--text)' }}>
        <CountUp end={value || 0} duration={1.2} />
        {suffix}
      </div>
    </div>
  );
}

function EmptyBlock({ text, height = 120 }) {
  return (
    <div className="flex items-center justify-center ox-text-dim text-sm" style={{ height }}>
      {text}
    </div>
  );
}
